import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell, CheckCheck, ClipboardList, CalendarClock, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import "../css/notificationbell.css";

const API = "https://apicrm.zenrealestate.in/api/notifications";
const POLL_MS = 30000;

const typeIcon = (type) => {
  switch (type) {
    case "task_assigned":
      return <ClipboardList size={16} />;
    case "visit_reminder":
      return <CalendarClock size={16} />;
    default:
      return <Info size={16} />;
  }
};

const timeAgo = (date) => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIds = useRef(null); // Set of ids already seen (for toast on new)
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const authHeader = () => ({
    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
  });

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(API, authHeader());
      const data = res.data.data || [];
      setItems(data);
      setUnread(res.data.unreadCount || 0);

      // Toast for newly arrived unread notifications (skip the first load)
      const currentIds = new Set(data.map((n) => n._id));
      if (seenIds.current) {
        data
          .filter((n) => !n.read && !seenIds.current.has(n._id))
          .forEach((n) => toast.info(`🔔 ${n.title}`));
      }
      seenIds.current = currentIds;
    } catch (err) {
      // Silently ignore (e.g. token missing on public pages)
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleOpenItem = async (n) => {
    try {
      if (!n.read) {
        await axios.patch(`${API}/${n._id}/read`, {}, authHeader());
      }
    } catch (err) {
      /* ignore */
    }
    setOpen(false);
    fetchNotifications();
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/read-all`, {}, authHeader());
      fetchNotifications();
    } catch (err) {
      /* ignore */
    }
  };

  return (
    <div className="nb-wrap" ref={panelRef}>
      <button
        className="nb-btn"
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && <span className="nb-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="nb-panel">
          <div className="nb-panel-header">
            <span>Notifications</span>
            {unread > 0 && (
              <button className="nb-markall" onClick={markAllRead}>
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>

          <div className="nb-list">
            {items.length === 0 ? (
              <div className="nb-empty">You're all caught up</div>
            ) : (
              items.map((n) => (
                <button
                  key={n._id}
                  className={"nb-item" + (n.read ? "" : " unread")}
                  onClick={() => handleOpenItem(n)}
                >
                  <span className={"nb-item-icon nb-" + n.type}>{typeIcon(n.type)}</span>
                  <span className="nb-item-body">
                    <span className="nb-item-title">{n.title}</span>
                    {n.message && <span className="nb-item-msg">{n.message}</span>}
                    <span className="nb-item-time">{timeAgo(n.createdAt)}</span>
                  </span>
                  {!n.read && <span className="nb-dot" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
